import { useState, useEffect, useRef } from 'react'
import { Loader2, AlertCircle, Bot, CheckCircle2, AlertTriangle, XCircle, Database } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@/components/ui/sheet'
import type {
  AgentRegistrationRequest,
  AgentUpdateRequest,
  AgentResponse,
  DatabaseConnectionConfig,
  CheckBaseUrlResponse,
} from '@/types/api'

interface AgentRegistrationSliderProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: AgentRegistrationRequest | AgentUpdateRequest) => Promise<void>
  agent?: AgentResponse | null
  mode: 'create' | 'edit'
  checkBaseUrl: (baseUrl: string, excludeAgentId?: string) => Promise<CheckBaseUrlResponse>
  getDbConnection: (agentId: string) => Promise<{
    db_type: string
    host: string
    port: number
    database_name: string
    username?: string | null
    ssl_mode: string
  }>
  fetchAgentInfo: (baseUrl: string) => Promise<{ description?: string } | null>
  testConnection?: (agentId: string) => Promise<{ connected: boolean }>
  testConnectionAdhoc?: (config: DatabaseConnectionConfig) => Promise<{ connected: boolean; message: string }>
}

type BaseUrlStatus = 'idle' | 'checking' | 'available' | 'unreachable' | 'conflict'

export function AgentRegistrationSlider({
  open,
  onOpenChange,
  onSubmit,
  agent,
  mode,
  checkBaseUrl,
  getDbConnection,
  fetchAgentInfo,
  testConnection,
  testConnectionAdhoc,
}: AgentRegistrationSliderProps) {
  const [formData, setFormData] = useState<AgentRegistrationRequest>({
    name: '',
    version: '1',
    base_url: '',
    agent_type: 'generic',
  })
  const [dbConnection, setDbConnection] = useState<DatabaseConnectionConfig>({
    db_type: 'postgresql',
    host: '',
    port: 5432,
    database_name: '',
    username: '',
    password: '',
    ssl_mode: 'prefer',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [agentDescription, setAgentDescription] = useState<string | null>(null)
  const [descriptionLoading, setDescriptionLoading] = useState(false)

  // Base URL validation state
  const [baseUrlStatus, setBaseUrlStatus] = useState<BaseUrlStatus>('idle')
  const [baseUrlMessage, setBaseUrlMessage] = useState<string | null>(null)
  const baseUrlTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Test connection state
  const [testingDb, setTestingDb] = useState(false)
  const [dbTestResult, setDbTestResult] = useState<{ connected: boolean; message?: string } | null>(null)

  // Track original agent type for type-change confirmation
  const [originalAgentType, setOriginalAgentType] = useState<string>('generic')
  const [showTypeChangeWarning, setShowTypeChangeWarning] = useState(false)

  useEffect(() => {
    if (!open) return

    if (mode === 'edit' && agent) {
      const agentType = agent.agent_type || 'generic'
      setFormData({
        name: agent.name,
        version: agent.version || '1',
        base_url: agent.base_url,
        agent_type: agentType,
      })
      setOriginalAgentType(agentType)
      setBaseUrlStatus('idle')
      setBaseUrlMessage(null)

      // Load DB connection for SQL agents
      if (agentType === 'sql') {
        loadDbConnection(agent.agent_id)
      }

      // Fetch description from remote agent
      loadAgentDescription(agent.base_url)
    } else {
      setFormData({
        name: '',
        version: '1',
        base_url: '',
        agent_type: 'generic',
      })
      setDbConnection({
        db_type: 'postgresql',
        host: '',
        port: 5432,
        database_name: '',
        username: '',
        password: '',
        ssl_mode: 'prefer',
      })
      setOriginalAgentType('generic')
      setAgentDescription(null)
      setBaseUrlStatus('idle')
      setBaseUrlMessage(null)
    }
    setError(null)
    setShowTypeChangeWarning(false)
  }, [mode, agent, open])

  const loadDbConnection = async (agentId: string) => {
    try {
      const conn = await getDbConnection(agentId)
      setDbConnection({
        db_type: conn.db_type,
        host: conn.host,
        port: conn.port,
        database_name: conn.database_name,
        username: conn.username || '',
        password: '',
        ssl_mode: conn.ssl_mode,
      })
    } catch {
      // No connection found, leave defaults
    }
  }

  const loadAgentDescription = async (baseUrl: string) => {
    setDescriptionLoading(true)
    try {
      const info = await fetchAgentInfo(baseUrl)
      setAgentDescription(info?.description || null)
    } catch {
      setAgentDescription(null)
    } finally {
      setDescriptionLoading(false)
    }
  }

  const handleBaseUrlBlur = () => {
    const url = formData.base_url.trim()
    if (!url) {
      setBaseUrlStatus('idle')
      setBaseUrlMessage(null)
      return
    }

    // Validate URL format
    try {
      new URL(url)
    } catch {
      setBaseUrlStatus('idle')
      setBaseUrlMessage(null)
      return
    }

    if (baseUrlTimeoutRef.current) clearTimeout(baseUrlTimeoutRef.current)

    baseUrlTimeoutRef.current = setTimeout(async () => {
      setBaseUrlStatus('checking')
      setBaseUrlMessage(null)
      try {
        const excludeId = mode === 'edit' && agent ? agent.agent_id : undefined
        const result = await checkBaseUrl(url, excludeId)

        if (!result.available) {
          setBaseUrlStatus('conflict')
          setBaseUrlMessage(`URL already in use by "${result.conflict_agent_name}"`)
        } else if (!result.reachable) {
          setBaseUrlStatus('unreachable')
          setBaseUrlMessage('Agent is not reachable at this URL')
        } else {
          setBaseUrlStatus('available')
          setBaseUrlMessage('Agent reachable')
        }
      } catch {
        setBaseUrlStatus('idle')
      }
    }, 300)
  }

  const handleAgentTypeChange = (newType: string) => {
    if (mode === 'edit' && originalAgentType === 'sql' && newType === 'generic') {
      setShowTypeChangeWarning(true)
    } else {
      setShowTypeChangeWarning(false)
    }

    setFormData({ ...formData, agent_type: newType as 'generic' | 'sql' | 'tool' })

    // Reset DB connection when switching to SQL on edit
    if (newType === 'sql' && mode === 'edit' && agent && originalAgentType === 'sql') {
      loadDbConnection(agent.agent_id)
    } else if (newType === 'sql') {
      setDbConnection({
        db_type: 'postgresql',
        host: '',
        port: 5432,
        database_name: '',
        username: '',
        password: '',
        ssl_mode: 'prefer',
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Block save if base URL has a conflict
    if (baseUrlStatus === 'conflict') {
      setError('Cannot save: base URL is already in use by another agent.')
      setLoading(false)
      return
    }

    try {
      const isSql = formData.agent_type === 'sql'
      const dbTouched =
        dbConnection.host.trim() !== '' ||
        dbConnection.database_name.trim() !== '' ||
        !!dbConnection.password?.trim()

      const shouldSendDb = isSql && (mode === 'create' || dbTouched)

      if (shouldSendDb) {
        if (dbConnection.db_type !== 'sqlite' && !dbConnection.host?.trim()) {
          setError('Database host is required for SQL agents (except SQLite).')
          setLoading(false)
          return
        }
        if (!dbConnection.database_name?.trim()) {
          setError('Database name is required for SQL agents.')
          setLoading(false)
          return
        }
      }

      const sqlConn =
        shouldSendDb
          ? {
              ...dbConnection,
              host:
                dbConnection.db_type === 'sqlite'
                  ? (dbConnection.host?.trim() || 'localhost')
                  : dbConnection.host.trim(),
              // Don't send empty password on edit (keep existing)
              ...(mode === 'edit' && !dbConnection.password?.trim()
                ? { password: undefined }
                : {}),
            }
          : undefined

      const data: AgentRegistrationRequest | AgentUpdateRequest = {
        ...formData,
        ...(sqlConn ? { database_connection: sqlConn } : {}),
      }

      await onSubmit(data)
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit form')
    } finally {
      setLoading(false)
    }
  }

  const baseUrlStatusIcon = () => {
    switch (baseUrlStatus) {
      case 'checking':
        return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      case 'available':
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
      case 'unreachable':
        return <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
      case 'conflict':
        return <XCircle className="h-3.5 w-3.5 text-destructive" />
      default:
        return null
    }
  }

  const baseUrlStatusColor = () => {
    switch (baseUrlStatus) {
      case 'available': return 'text-green-600'
      case 'unreachable': return 'text-yellow-600'
      case 'conflict': return 'text-destructive'
      default: return 'text-muted-foreground'
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        {/* Header */}
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bot className="h-5 w-5 text-primary" />
            </div>
            <div>
              <SheetTitle className="text-base">
                {mode === 'create' ? 'Register New Agent' : 'Edit Agent'}
              </SheetTitle>
              <SheetDescription className="text-xs mt-0.5">
                {mode === 'create'
                  ? 'Add a new agent to the platform'
                  : 'Update agent configuration'}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>
        <SheetClose onClose={() => onOpenChange(false)} />

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <form id="agent-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 px-3 py-2.5 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Section: Basic Info */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Basic Information
              </h4>

              <div className="space-y-1">
                <label htmlFor="name" className="text-xs font-medium text-foreground">
                  Name <span className="text-destructive">*</span>
                </label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Agent"
                  required
                  className="h-9 text-sm"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="base_url" className="text-xs font-medium text-foreground">
                  Base URL <span className="text-destructive">*</span>
                </label>
                <Input
                  id="base_url"
                  type="url"
                  value={formData.base_url}
                  onChange={(e) => {
                    setFormData({ ...formData, base_url: e.target.value })
                    setBaseUrlStatus('idle')
                    setBaseUrlMessage(null)
                  }}
                  onBlur={handleBaseUrlBlur}
                  placeholder="http://agent:8080"
                  required
                  className="h-9 text-sm"
                />
                {(baseUrlStatus !== 'idle') && (
                  <div className={`flex items-center gap-1.5 mt-1 text-xs ${baseUrlStatusColor()}`}>
                    {baseUrlStatusIcon()}
                    <span>{baseUrlMessage}</span>
                  </div>
                )}
              </div>

              {/* Description (read-only on edit, fetched from remote agent) */}
              {mode === 'edit' && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">
                    Agent Description
                  </label>
                  {descriptionLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Fetching from agent...
                    </div>
                  ) : agentDescription ? (
                    <Textarea
                      value={agentDescription}
                      readOnly
                      className="text-sm bg-muted/50 min-h-[60px] resize-none cursor-default"
                    />
                  ) : (
                    <p className="text-xs text-muted-foreground py-1">
                      Could not fetch agent description
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Agent Type */}
            <div className="space-y-1">
              <label htmlFor="agent_type" className="text-xs font-medium text-foreground">
                Agent Type
              </label>
              <select
                id="agent_type"
                value={formData.agent_type || 'generic'}
                onChange={(e) => handleAgentTypeChange(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="generic">Generic</option>
                <option value="sql">SQL (Database-backed)</option>
                <option value="tool">Tool (Internal helper)</option>
              </select>
            </div>

            {/* Tool type info note */}
            {formData.agent_type === 'tool' && (
              <div className="flex items-start gap-2 rounded-lg bg-purple-50 border border-purple-200 px-3 py-2.5 text-sm text-purple-800 dark:bg-purple-900/20 dark:border-purple-800 dark:text-purple-300">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Tools are internal helper agents used by the orchestrator. They won't appear in the
                  chat agent selector and don't require a dedicated database connection.
                </span>
              </div>
            )}

            {/* Type change warning */}
            {showTypeChangeWarning && (
              <div className="flex items-start gap-2 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2.5 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Changing to Generic will permanently remove all database connections,
                  schema data, and relationships for this agent.
                </span>
              </div>
            )}

            {/* Database Connection (shown for SQL type) */}
            {formData.agent_type === 'sql' && (
              <>
                <div className="h-px bg-border" />
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Database Connection
                  </h4>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label htmlFor="db_type" className="text-xs font-medium text-foreground">
                        Database Type <span className="text-destructive">*</span>
                      </label>
                      <select
                        id="db_type"
                        value={dbConnection.db_type}
                        onChange={(e) =>
                          setDbConnection({ ...dbConnection, db_type: e.target.value })
                        }
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      >
                        <option value="postgresql">PostgreSQL</option>
                        <option value="mysql">MySQL</option>
                        <option value="sqlite">SQLite</option>
                        <option value="mssql">MSSQL</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label htmlFor="db_host" className="text-xs font-medium text-foreground">
                        Host <span className="text-destructive">*</span>
                      </label>
                      <Input
                        id="db_host"
                        value={dbConnection.host}
                        onChange={(e) =>
                          setDbConnection({ ...dbConnection, host: e.target.value })
                        }
                        placeholder="localhost"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label htmlFor="db_port" className="text-xs font-medium text-foreground">
                        Port <span className="text-destructive">*</span>
                      </label>
                      <Input
                        id="db_port"
                        type="number"
                        value={dbConnection.port}
                        onChange={(e) =>
                          setDbConnection({
                            ...dbConnection,
                            port: parseInt(e.target.value) || 5432,
                          })
                        }
                        placeholder="5432"
                        className="h-9 text-sm"
                      />
                    </div>

                    <div className="space-y-1 col-span-2">
                      <label htmlFor="db_name" className="text-xs font-medium text-foreground">
                        Database Name <span className="text-destructive">*</span>
                      </label>
                      <Input
                        id="db_name"
                        value={dbConnection.database_name}
                        onChange={(e) =>
                          setDbConnection({
                            ...dbConnection,
                            database_name: e.target.value,
                          })
                        }
                        placeholder="mydb"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label htmlFor="db_username" className="text-xs font-medium text-foreground">
                        Username
                      </label>
                      <Input
                        id="db_username"
                        value={dbConnection.username || ''}
                        onChange={(e) =>
                          setDbConnection({ ...dbConnection, username: e.target.value })
                        }
                        placeholder="postgres"
                        className="h-9 text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <label htmlFor="db_password" className="text-xs font-medium text-foreground">
                        Password
                      </label>
                      <Input
                        id="db_password"
                        type="password"
                        value={dbConnection.password || ''}
                        onChange={(e) =>
                          setDbConnection({ ...dbConnection, password: e.target.value })
                        }
                        placeholder={mode === 'edit' ? 'Leave blank to keep current' : '********'}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>

                  {/* Test Connection Button */}
                  <div className="flex items-center gap-3 pt-2 border-t border-border">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={testingDb || !dbConnection.host?.trim() || !dbConnection.database_name?.trim()}
                      onClick={async () => {
                        setTestingDb(true)
                        setDbTestResult(null)
                        try {
                          if (mode === 'edit' && agent && testConnection) {
                            const res = await testConnection(agent.agent_id)
                            setDbTestResult({ connected: res.connected })
                          } else if (testConnectionAdhoc) {
                            const res = await testConnectionAdhoc(dbConnection)
                            setDbTestResult(res)
                          }
                        } catch (err) {
                          setDbTestResult({ connected: false, message: err instanceof Error ? err.message : 'Test failed' })
                        } finally {
                          setTestingDb(false)
                        }
                      }}
                      className="h-8"
                    >
                      {testingDb ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Database className="h-3.5 w-3.5 mr-1.5" />
                          Test Connection
                        </>
                      )}
                    </Button>
                    {dbTestResult !== null && (
                      <div className="flex items-center gap-1.5 text-sm">
                        {dbTestResult.connected ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span className="text-green-600">Connected</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-destructive" />
                            <span className="text-destructive text-xs">{dbTestResult.message || 'Connection failed'}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </form>
        </div>

        {/* Footer actions */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="h-9"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="agent-form"
            size="sm"
            disabled={loading || baseUrlStatus === 'conflict'}
            className="h-9 min-w-[100px]"
          >
            {loading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Saving...
              </>
            ) : (
              mode === 'create' ? 'Register Agent' : 'Update Agent'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
